'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  bookingAppointment,
  geRecommendations,
  validatePromoCode
} from 'actions/services';
import config from 'config';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import {
  useCalculateAppointmentPriceQuery,
  useGetApptConfirmationSettingsQuery,
  useGetApptDepositPrefsQuery
} from 'services/bookingApi';

import { Box, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setApptCalendarDate,
  setCancellationPolicyModalOpen,
  setComments,
  setPromoCode,
  setPromoData
} from 'store/reducers/bookingAppointmentSlice';
import { setError, setPromoErrorType } from 'store/reducers/servicesSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import {
  formatPhoneNumbers,
  formatPhoneToFormat,
  getPromoErrorMessage
} from 'utils';
import { isInvalidStoreQueryValue } from 'utils/bookingSearchParams';

import {
  AppointmentInterface,
  BookingParams,
  PromoErrorType,
  SearchParameters,
  SelectedGiftCardType
} from 'types';

import { ContactUsErrorModal, PromoCode, SimpleErrorModal } from 'components';
import { Button, Divider, Link, Loader, MultiStepHeader } from 'ui';
import {
  CardsPaymentsList,
  CheckboxRules,
  CommentsBlock,
  DepositRequiredMessage,
  ReservationList
} from './components';
import { CancellationPolicyModal } from '../CancellationPolicyModal';

const {
  booking: {
    isCancellationPolicyRequired,
    review: {
      title,
      descriptionStart,
      descriptionMiddle,
      descriptionEnd,
      descriptionMoveEnd,
      rulesTitle,
      commentsBlockTitle,
      paddings
    }
  },
  contacts: { phone }
} = config;

interface ReviewStepProps {
  onBack: () => void;
  onNext: (isExist: boolean) => void;
}

const ReviewStep = ({ onBack, onNext }: ReviewStepProps) => {
  const {
    colors: {
      palette: {
        booking: { stepReview }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const { user, selectedLocation } = useAppSelector((state) => state.user);
  const { isLoading, errorMsg, promoErroType } = useAppSelector(
    (state) => state.services
  );
  const {
    selectedAppointment,
    selectedServices,
    promoData,
    moveApptId,
    promoCode,
    depositAmount,
    selectedDepositGiftCards,
    comments
  } = useAppSelector((state) => state.bookingAppointment);
  const { isLoading: isClientLoading } = useAppSelector(
    (state) => state.client
  );

  const {
    data: bookingConfirmation,
    isLoading: isApptConfirmationSettingsLoading
  } = useGetApptConfirmationSettingsQuery('');

  const isConfirmationTextExist = bookingConfirmation?.book_confirm_text;
  const isConfirmationRequired = bookingConfirmation?.req_book_confirm;
  const isCConFileRequired = bookingConfirmation?.cc_on_file_require;

  const [isRulesAccepted, setIsRulesAccepted] = useState(
    !isConfirmationRequired
  );

  const servicesId = useMemo(
    () => (selectedServices ?? []).map(({ id }) => id.toString()),
    [selectedServices]
  );
  const [storeId] = useQueryState(SearchParameters.Store);
  const [serviceIds] = useQueryState(
    SearchParameters.Services,
    parseAsArrayOf(parseAsString).withDefault(servicesId)
  );

  const endDescription = moveApptId ? descriptionMoveEnd : descriptionEnd;
  const locationId =
    storeId != null && !isInvalidStoreQueryValue(storeId)
      ? Number(storeId)
      : selectedLocation?.id ?? 0;

  const locationPhone = selectedLocation?.phone || phone;
  const formattedPhone = formatPhoneNumbers(locationPhone);
  const attendeeId = user?.id || 0;

  const { data: depositOptions, isLoading: isDepositPrefsLoading } =
    useGetApptDepositPrefsQuery(
      { svc_list: serviceIds, locid: locationId },
      {
        skip: !serviceIds || !locationId
      }
    );
  const { data: apptPrice, isLoading: isCalculatePriceLoading } =
    useCalculateAppointmentPriceQuery(
      {
        appt: selectedAppointment as AppointmentInterface,
        locationId: selectedAppointment?.store as number,
        attendees: [attendeeId]
      },
      {
        skip: selectedAppointment === null || !selectedAppointment?.store
      }
    );

  const handleCheckBoxChange = (value: boolean) => {
    setIsRulesAccepted(value);
  };

  const handleCommentsChange = (value: string) => {
    dispatch(setComments(value));
  };

  const removeErrorPromoCode = () => {
    dispatch(setPromoErrorType(PromoErrorType.NO_ERROR));
  };

  const handleRemoveDiscount = () => {
    dispatch(setPromoData(null));
    dispatch(setPromoCode(''));
    removeErrorPromoCode();
  };

  const handleSetPromo = async (promoCodeValue: string) => {
    if (selectedAppointment && promoCodeValue) {
      const response = await dispatch(
        validatePromoCode(promoCodeValue, selectedAppointment)
      );
      handleRemoveDiscount();

      if (response?.is_valid) {
        dispatch(setPromoCode(promoCodeValue));
        dispatch(setPromoData(response));
      } else {
        dispatch(
          setPromoErrorType(
            response.promo_error_type ?? PromoErrorType.DOES_NOT_EXIST
          )
        );
      }
    }
  };

  const bookingParams = useMemo(() => {
    const promoParams =
      promoData?.is_valid && moveApptId === null
        ? { promo_code_id: promoData?.code_id, promo_code: promoCode }
        : {};
    const moveApptParams = moveApptId ? { move_appt_id: moveApptId } : {};

    const params: BookingParams = {
      appt: selectedAppointment!,
      locid: Number(locationId),
      ...promoParams,
      ...moveApptParams
    };

    if (depositOptions?.req_deposit && moveApptId === null) {
      const depositGcard: SelectedGiftCardType[] = selectedDepositGiftCards.map(
        ({ id, amt_balance }) => ({ gc_id: id, amount: amt_balance })
      );

      params.deposit_amt = depositOptions?.deposit_amount;
      params.charge_deposit = depositOptions?.req_deposit;
      params.deposit_gcard = depositGcard;
    } else if (depositOptions?.req_guarantee && moveApptId === null) {
      params.guarantee_amt = depositOptions?.guarantee_amount;
      params.store_guarantee = depositOptions?.req_guarantee;
    }

    return params;
  }, [
    promoData,
    promoCode,
    moveApptId,
    selectedAppointment,
    locationId,
    depositOptions,
    selectedDepositGiftCards
  ]);

  const handleSubmit = async (id?: number | null) => {
    if (selectedAppointment) {
      if (moveApptId === null && Boolean(id)) {
        if (depositOptions?.req_deposit) {
          bookingParams.deposit_ccard_id = id as number;
        } else if (depositOptions?.req_guarantee) {
          bookingParams.payment_id = id as number;
        }
      }
      const { isSuccess, apptId, apptCalendarDate } = await dispatch(
        bookingAppointment(bookingParams, comments)
      );
      dispatch(setComments(comments));
      dispatch(setApptCalendarDate(apptCalendarDate));
      if (isSuccess) {
        const reccomendedProducts = apptId
          ? await dispatch(geRecommendations(apptId ?? ''))
          : [];
        onNext(Boolean(reccomendedProducts.length));
      }
    }
  };

  useEffect(() => {
    if (!selectedAppointment || !user) {
      onBack();
    }
    return () => {
      dispatch(setRedirectFlow(null));
    };
  }, [dispatch, onBack, selectedAppointment, user]);

  const handleCloseModal = () => dispatch(setError(''));

  const onBookNow = () => {
    if (isCancellationPolicyRequired) {
      dispatch(setCancellationPolicyModalOpen(true));
    } else {
      handleSubmit();
    }
  };

  const promoErrorText = getPromoErrorMessage(promoErroType, true);

  return (
    <Stack w='100%' mx='auto' gap={0}>
      <MultiStepHeader
        title={title}
        subTitle={`${descriptionStart || ''} ${
          isConfirmationRequired ? descriptionMiddle : ''
        } ${endDescription || ''}`}
        headingColor={stepReview.headingColor}
        descriptionColor={stepReview.descriptionColor}
        mt={['50px', '60px']}
        mb={['10px', '15px', '20px']}
        descriptionPX={paddings}
      />
      <Divider
        h='1px'
        bg={stepReview.dividerColor}
        border='none'
        mt='10px'
        mb='20px'
      />
      <Box>
        {selectedAppointment && apptPrice && (
          <ReservationList
            onRemoveDiscount={handleRemoveDiscount}
            selectedAppointment={selectedAppointment}
            promoData={promoData}
            promoCode={promoCode}
            apptPrice={apptPrice}
            isInitialOpen={
              !(
                Boolean(depositOptions?.req_deposit) ||
                Boolean(depositOptions?.req_guarantee)
              ) || moveApptId !== null
            }
          >
            <PromoCode
              onSaveDiscount={handleSetPromo}
              isError={!!errorMsg}
              onClearError={removeErrorPromoCode}
            />

            <Divider
              top='0'
              h='1px'
              bg={stepReview.dividerColor}
              mt='20px'
              mb='8px'
              border='none'
            />

            {isConfirmationTextExist && !isCancellationPolicyRequired && (
              <>
                <CheckboxRules
                  onChange={handleCheckBoxChange}
                  value={isRulesAccepted}
                  label={rulesTitle}
                  helperText={bookingConfirmation.book_confirm_text}
                  isRenderCheckbox={bookingConfirmation?.req_book_confirm}
                />
                <Divider
                  top='0'
                  h='1px'
                  bg={stepReview.dividerColor}
                  mb='20px'
                  mt='8px'
                  border='none'
                />
              </>
            )}

            <CommentsBlock
              label={commentsBlockTitle}
              value={comments}
              onChange={handleCommentsChange}
            />
          </ReservationList>
        )}
      </Box>
      {(isCConFileRequired ||
        Boolean(depositOptions?.req_deposit) ||
        Boolean(depositOptions?.req_guarantee)) &&
      moveApptId === null ? (
        <>
          {Boolean(depositOptions?.req_deposit) ||
          Boolean(depositOptions?.req_guarantee) ? (
            <DepositRequiredMessage
              sum={depositAmount}
              options={depositOptions}
            />
          ) : (
            <Box h='20px' />
          )}
          <CardsPaymentsList
            isButtonDisabled={
              !selectedAppointment || !isRulesAccepted || depositAmount !== 0
            }
            onPaymentConfirmation={handleSubmit}
            onBack={onBack}
            depositOptions={depositOptions}
          >
            <Text
              textAlign='center'
              color={stepReview.assistanceTextColor}
              fontSize='14px'
              mt={{ base: '32px', md: '25px' }}
            >
              If you need assistance, please call us at{' '}
              <Link
                to={`tel:${formattedPhone}`}
                sx={{
                  fontSize: '14px',
                  color: stepReview.phoneLinkColor,
                  fontFamily: 'main',
                  fontWeight: 'bold',
                  _hover: { textDecoration: 'underline' }
                }}
              >
                {formatPhoneToFormat(formattedPhone)}
              </Link>
            </Text>
          </CardsPaymentsList>
        </>
      ) : (
        <Flex
          direction={{ base: 'column-reverse', md: 'row' }}
          mt={{ base: '30px', md: '50px' }}
          justifyContent='center'
          alignItems='center'
          gap={{ base: '20px', md: '30px' }}
          w='100%'
        >
          <Button variant='outline' w='150px' onClick={onBack}>
            Back
          </Button>
          <Button
            w='150px'
            isDisabled={!selectedAppointment || !isRulesAccepted}
            onClick={onBookNow}
          >
            {moveApptId ? 'Move Now' : 'Book Now'}
          </Button>
        </Flex>
      )}
      {(isLoading ||
        isDepositPrefsLoading ||
        isCalculatePriceLoading ||
        isApptConfirmationSettingsLoading ||
        isClientLoading) && <Loader fixed bg='blackOpacity.10' />}
      <SimpleErrorModal
        isOpen={!!errorMsg}
        onClose={handleCloseModal}
        description={errorMsg}
      />
      <ContactUsErrorModal
        errorText={promoErrorText}
        onClearHandler={removeErrorPromoCode}
      />
      <CancellationPolicyModal onConfirm={handleSubmit} />
    </Stack>
  );
};

export default ReviewStep;
